import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useDemoState } from "@/data/demoState";
import { surveyorStats } from "@/data/workspaceData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Package, Eye, MessageSquare, CheckCircle2, ClipboardCheck,
  ArrowRight, FileCheck, Inbox
} from "lucide-react";

const quickLinks = [
  { label: "Incoming Packs", path: "/surveyor/incoming-packs", icon: Package, desc: "Review packs from builders" },
  { label: "Review Centre", path: "/surveyor/review-centre", icon: Eye, desc: "Review evidence against conditions" },
  { label: "Closeout View", path: "/surveyor/closeout-view", icon: FileCheck, desc: "Final pack summaries" },
];

export default function SurveyorOverview() {
  const { evidence, packs } = useDemoState();

  const statCards = [
    { label: "Incoming Packs", value: packs.filter(p => p.status !== 'complete').length, icon: Package, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
    { label: "Evidence Submitted", value: evidence.length, icon: Inbox, color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-100" },
    { label: "Clarifications", value: surveyorStats.clarificationsRequested, icon: MessageSquare, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
    { label: "Accepted This Week", value: evidence.filter(e => e.status === 'approved').length, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
  ];

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="surveyor-overview">
        {/* Explainer */}
        <Card className="border-blue-100 bg-blue-50/50" data-testid="surveyor-explainer">
          <CardContent className="py-5 px-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                <ClipboardCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-base text-slate-900 mb-1">What easypermit does for surveyors</h3>
                <ul className="space-y-1">
                  {[
                    "Receive cleaner incoming packs with evidence linked to conditions",
                    "See source excerpts from decision letters alongside evidence",
                    "Fast request-more workflow without email back and forth",
                    "Review layer only. Certification decisions remain outside this system.",
                  ].map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="surveyor-stats">
          {statCards.map((s) => (
            <Card key={s.label} className={`${s.bg} ${s.border} border`}>
              <CardContent className="py-4 px-5">
                <div className="flex items-center justify-between mb-2">
                  <s.icon className={`w-5 h-5 ${s.color}`} />
                  <span className="text-xs font-medium text-slate-500">{s.label}</span>
                </div>
                <p className={`text-3xl font-heading font-bold ${s.color}`}>{s.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="surveyor-quick-links">
          {quickLinks.map((link) => (
            <Link key={link.path} to={link.path}>
              <Card className="border-slate-200 hover:border-blue-200 hover:shadow-md transition-all cursor-pointer group h-full">
                <CardContent className="py-5 px-5 flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors">
                    <link.icon className="w-5 h-5 text-slate-500 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">{link.label}</p>
                    <p className="text-xs text-slate-400">{link.desc}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors shrink-0" />
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent evidence */}
        <Card className="border-slate-200" data-testid="recent-evidence">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-heading">Recent evidence submitted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {evidence.slice(0, 4).map((ev) => (
                <div key={ev.id} className="flex items-center justify-between py-2.5 px-3 bg-slate-50/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-[10px] font-medium text-slate-600">
                      {ev.uploadedBy.split(" ").map(n => n[0]).join("")}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{ev.fileName}</p>
                      <p className="text-xs text-slate-400">{ev.uploadedBy} - {ev.project}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">{ev.uploadDate}</span>
                    <StatusBadge status={ev.status} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
